import { Injectable } from '@angular/core';
import { AmplifyApiAdapterService } from '../adapters/amplify-api-adapter.service';

type QueryParamValue = string | number | boolean;

interface ApiOptions<TBody = unknown> {
  body?: TBody;
  queryStringParameters?: Record<string, QueryParamValue>;
  headers?: Record<string, string>;
}

type ApiGetOptions = Omit<ApiOptions, 'body'>;

@Injectable({
  providedIn: 'root'
})
export class AmplifyApiService {
  constructor(private readonly amplifyApiAdapter: AmplifyApiAdapterService) {}

  private buildQueryParams(
    queryStringParameters?: Record<string, QueryParamValue>
  ): Record<string, string> | undefined {
    return queryStringParameters
      ? Object.fromEntries(Object.entries(queryStringParameters).map(([key, value]) => [key, String(value)]))
      : undefined;
  }

  private buildBody<TBody>(body: TBody | undefined): unknown {
    if (body === undefined) {
      return undefined;
    }

    if (body instanceof FormData) {
      return body;
    }

    return JSON.parse(JSON.stringify(body));
  }

  public get = async <TResponse = any>(apiName: string, path: string, options?: ApiGetOptions): Promise<TResponse> => {
    const { body } = await this.amplifyApiAdapter.get({
      apiName,
      path,
      options: {
        queryParams: this.buildQueryParams(options?.queryStringParameters),
        headers: options?.headers
      }
    }).response;

    return body.json() as Promise<TResponse>;
  };

  public post = async <TResponse = any, TBody = unknown>(
    apiName: string,
    path: string,
    options?: ApiOptions<TBody>
  ): Promise<TResponse> => {
    const { body } = await this.amplifyApiAdapter.post({
      apiName,
      path,
      options: {
        body: this.buildBody(options?.body) as never,
        queryParams: this.buildQueryParams(options?.queryStringParameters),
        headers: options?.headers
      }
    }).response;

    return body.json() as Promise<TResponse>;
  };

  public put = async <TResponse = any, TBody = unknown>(
    apiName: string,
    path: string,
    options?: ApiOptions<TBody>
  ): Promise<TResponse> => {
    const { body } = await this.amplifyApiAdapter.put({
      apiName,
      path,
      options: {
        body: this.buildBody(options?.body) as never,
        queryParams: this.buildQueryParams(options?.queryStringParameters),
        headers: options?.headers
      }
    }).response;

    return body.json() as Promise<TResponse>;
  };

  public patch = async <TResponse = any, TBody = unknown>(
    apiName: string,
    path: string,
    options?: ApiOptions<TBody>
  ): Promise<TResponse> => {
    const { body } = await this.amplifyApiAdapter.patch({
      apiName,
      path,
      options: {
        body: this.buildBody(options?.body) as never,
        queryParams: this.buildQueryParams(options?.queryStringParameters),
        headers: options?.headers
      }
    }).response;

    return body.json() as Promise<TResponse>;
  };

  public delete = async <TResponse = any, TBody = unknown>(
    apiName: string,
    path: string,
    options?: ApiOptions<TBody>
  ): Promise<TResponse> => {
    const response = await this.amplifyApiAdapter.del({
      apiName,
      path,
      options: {
        body: this.buildBody(options?.body) as never,
        queryParams: this.buildQueryParams(options?.queryStringParameters),
        headers: options?.headers
      }
    }).response;

    try {
      return (await response.body.json()) as TResponse;
    } catch {
      return undefined as TResponse;
    }
  };
}
